import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useStateWithLocalStorage } from '../utils';

import { routes } from '../utils/constants/routes';

const images = {
  mrp: '/images/quotes-img/mrp.jpg',
  mre: '/images/quotes-img/mre.svg',
  mri: '/images/quotes-img/mri.jpg',
  rcp: '/images/quotes-img/rcp.jpg',
  rce: '/images/quotes-img/rce.svg',
  prevoyance: '/images/quotes-img/prevoyance.jpg',
  mrb: '/images/quotes-img/mrb.jpg',
  pno: '/images/quotes-img/pno.jpg',
  pne: '/images/quotes-img/pne.jpg',
  association: '/images/quotes-img/asso.jpg',
  rcngawk: '/images/quotes-img/rcnga.jpg',
  do: '/images/quotes-img/rcp.jpg',
  rcphx: '/images/quotes-img/rcp.jpg',
  rcgls: '/images/quotes-img/rcgls.png'
};

const shortcutsCardImage = {
  mrp: '/images/quotes-img/mr-pro.svg',
  mre: '/images/quotes-img/mre.svg',
  mri: '/images/quotes-img/mri.svg',
  rcp: '/images/quotes-img/rc-pro.svg',
  rce: '/images/quotes-img/rce.svg',
  prevoyance: '/images/quotes-img/prevoyance.svg',
  mrb: '/images/quotes-img/mr-pro.svg',
  pno: '/images/quotes-img/mri.svg',
  pne: '/images/quotes-img/mri.svg',
  association: '/images/quotes-img/asso.svg',
  rcngawk: '/images/quotes-img/rcnga.svg',
  do: '/images/quotes-img/rc-pro.svg',
  rcphx: '/images/quotes-img/rcp.jpg',
  rcgls: '/images/quotes-img/rc-pro.svg'
};

const AuthContext = createContext({});

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL
});

axiosInstance.interceptors.request.use(
  (config) => {
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const AuthContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useStateWithLocalStorage('user');
  const [token, setToken] = useStateWithLocalStorage('token');
  const [refreshToken, setRefreshToken] =
    useStateWithLocalStorage('refresh_token');
  const [remember, setRememberMe] = useStateWithLocalStorage(
    'remember_me',
    !!refreshToken
  );
  const [isValid, setIsValid] = useState(!!refreshToken || !!token);
  const defaultGrantedRoutes = [
    'settings',
    'login',
    'email-confirmation',
    'profile',
    'home',
    '',
    '.'
  ];
  const [productAccess, setProductAccess] = useStateWithLocalStorage(
    'products',
    []
  );

  const arrayProductBlocked = [
    'MRPWK',
    'RCPWK',
    'MRPCM',
    'MRPML',
    'RCPCM',
    'RCPML',
    'RCPMK',
    'MRIWK',
    'MRIML',
    'PNOML',
    'DO'
  ];

  const setSession = (accessToken) => {
    if (accessToken) {
      setToken(accessToken);
      setIsValid(true);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      setIsValid(false);
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  };

  const checkIsGranted = (requestPath, authorisationLevel) => {
    const actions = ['show', 'edit', 'create'];
    let splitPath = (authorisationLevel || requestPath).split('.');

    // handle users without permission field and granted route by default
    if (!user?.permission || defaultGrantedRoutes.includes(splitPath[0])) {
      return true;
    }

    if (requestPath) {
      // Slice splitPath till action
      if (splitPath.length === 3 && !actions.includes(splitPath[2]))
        splitPath = splitPath.slice(0, 2);
      else splitPath = splitPath.slice(0, 3);

      // If splitPath does not have action, add list as default
      if (!actions.includes(splitPath[splitPath.length - 1]))
        splitPath.push('list');
    }

    const { permission } = user;
    let allowedPath = {};
    for (let idx = 0; idx < splitPath.length; idx += 1) {
      const p = splitPath[idx];
      allowedPath = allowedPath[p] || permission[p];
      if (!allowedPath) return false; // if permission does not have path or if it is false -> Unauthorised
      if (idx === splitPath.length - 1) {
        if (allowedPath) return true; // Done checking path, if permission still true -> Authorised
        return false;
      }
    }
    return false;
  };

  const checkShouldDisplayMenuItem = (requestedItem, parent) => {
    if (!user?.permission) {
      // handle user without permission field
      return true;
    }
    return !!(
      user?.permission[requestedItem] ||
      (user?.permission[parent] &&
        user?.permission[parent][requestedItem] &&
        user?.permission[parent][requestedItem].list) ||
      defaultGrantedRoutes.includes(requestedItem)
    );
  };

  const checkRightMenuAccess = (_item) => {
    let item = _item;
    if (['quotations', 'contracts'].includes(item)) return true;
    if (item === 'claims') item = 'sinister';
    if (user?.role === 'users:USER' && user?.permissionManagement) {
      if (item in user.permissionManagement)
        return user.permissionManagement?.[item];
    }
    return true;
  };

  const getOrder = (title) => {
    switch (title) {
      case 'rcngawk':
        return 1;
      case 'rcp':
        return 2;
      case 'mrp':
        return 3;
      case 'mri':
        return 4;
      case 'mrb':
        return 5;
      case 'pno':
        return 6;
      case 'pne':
        return 7;
      case 'association':
        return 8;
      case 'prevoyance':
        return 9;
      default:
        return 0;
    }
  };

  const setProdAccess = (permissions) => {
    const products = permissions
      .filter(
        (p) => p.permitted && !arrayProductBlocked.includes(p.product.type)
      )
      .map((p) => ({
        title: p.product.type.toLowerCase(),
        path:
          p.product.type.toLowerCase() !== 'mri'
            ? p.product.type.toLowerCase()
            : 'mriml',
        description: t(`${p.product.type.toLowerCase()}.title`),
        image: images?.[p.product.type.toLowerCase()],
        productType: p.product.type.toLowerCase(),
        shortCutsImage: shortcutsCardImage?.[p.product.type.toLowerCase()],
        highlight: p.product.type.toLowerCase() === 'rcngawk',
        order: getOrder(p.product.type.toLowerCase()),
        advancedManagement: p.advancedManagement
      }));
    if (products.some((p) => p.title === 'mrp')) {
      const mrpProduct = products.find((p) => p.title === 'mrp');
      products.push({
        title: 'mrb',
        titleLink: 'mrb',
        search: '?mrpT=office&sup=wk',
        path: `${routes.QUOTES}/mrp?mrpT=office&sup=wk`,
        description: t(`mrb.title`),
        image: images.mrb,
        shortCutsImage: shortcutsCardImage.mrb,
        productType: 'mrb',
        highlight: false,
        order: getOrder('mrb'),
        advancedManagement: mrpProduct.advancedManagement
      });
    }

    if (products.some((p) => p.title === 'mri')) {
      const mriProduct = products.find((p) => p.title === 'mri');
      products.push(
        {
          title: 'pno',
          titleLink: 'pnoml',
          path: `${routes.QUOTES}/pnoml`,
          description: t(`pno.title`),
          highlight: false,
          productType: 'pno',
          image: images.pno,
          shortCutsImage: shortcutsCardImage?.pno,
          order: getOrder('pno'),
          advancedManagement: mriProduct.advancedManagement
        },
        {
          title: 'pne',
          titleLink: 'pnoml',
          path: `${routes.QUOTES}/pnoml`,
          description: t(`pne.title`),
          highlight: false,
          productType: 'pne',
          image: images.pne,
          shortcutsCardImage: shortcutsCardImage.pne,
          order: getOrder('pne'),
          advancedManagement: mriProduct.advancedManagement
        }
      );
    }

    setProductAccess(products);
  };

  const getProductAccess = (type) => {
    if (productAccess) {
      for (let i = 0; i < productAccess.length; i += 1) {
        if (productAccess[i].title === type) return productAccess[i];
      }
    }
    return null;
  };

  const productsPermissions = (_user) => {
    if (_user.permissionManagement) {
      const permissions = _user.permissionManagement.products_access;
      switch (_user.role) {
        case 'users:SUPER-USER':
          setProdAccess(permissions);
          break;
        case 'users:USER':
          // check if use direction profile ?
          if (_user.use_direction_profile) {
            setProdAccess(_user.refChef.permissionManagement.products_access);
          } else setProdAccess(permissions);
          break;
        default:
          break;
      }
    } else {
      // user do not permissions (GUEST or USER or ADMIN)
      // default permissions
    }
  };

  const loginAPI = async (email, password, rememberMe) => {
    const result = await axiosInstance.get('/login', {
      auth: {
        username: email,
        password
      }
    });
    setUser(result.data.user);
    setRememberMe(rememberMe);
    if (rememberMe) setRefreshToken(result.data.refresh_token);
    setSession(result.data.token);
    productsPermissions(result.data.user);
    return result;
  };

  const registerAPI = (values) => axiosInstance.post('/register', values);

  const logout = () => {
    setSession(null);
  };

  const isTokenValid = async () => {
    if (!token) {
      setSession(null);
      return false;
    }
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        if (remember && refreshToken) {
          const result = await axiosInstance.post('/token/refresh', {
            refresh_token: refreshToken
          });
          setToken(result.data.token);
          return { newToken: result.data.token };
        }
        setSession(null);
        return false;
      }
    } catch (e) {
      setSession(null);
      return false;
    }
    if (!isValid) {
      setIsValid(true);
    }
    return true;
  };

  useEffect(() => {
    (async () => {
      await isTokenValid();
    })();
    // eslint-disable-next-line
  }, []);

  const fetchAPI = async (
    url,
    method = 'GET',
    body = null,
    responseType = 'json',
    cancelToken
  ) => {
    const { newToken } = await isTokenValid();
    const result = await axiosInstance({
      url,
      method,
      responseType,
      cancelToken,
      data: body,
      headers: {
        Authorization: `Bearer ${newToken || token}`
      }
    });
    return result;
  };

  const dispatchAPI = (type, options) => {
    switch (type) {
      case 'LOGIN':
        return loginAPI(options.email, options.password, options.rememberMe);
      case 'REGISTER':
        return registerAPI(options);
      case 'LOGOUT':
        return logout();
      case 'GET':
        return fetchAPI(
          options.url,
          'GET',
          null,
          options.responseType,
          options.cancelToken
        );
      case 'DELETE':
        return fetchAPI(options.url, 'DELETE');
      case 'POST':
      case 'PATCH':
        return fetchAPI(options.url, type, options.body);
      default:
        throw new Error('Unknown dispatchAPI type!');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        remember,
        token,
        isValid,
        dispatchAPI,
        checkIsGranted,
        checkShouldDisplayMenuItem,
        productAccess,
        setProdAccess,
        checkRightMenuAccess,
        getProductAccess
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.element.isRequired
};

export default () => useContext(AuthContext);
